import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActiveBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import Pagination from '@/components/Pagination';
import { Plus, Search, Edit2, Trash2, Package, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const EMPTY = { name: '', model: '', sku: '', description: '', warrantyMonths: 12, slaDeadlineDays: '', isActive: true };

export default function ProductsPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const load = useCallback(async (page = 1) => {
    if (isSuperAdmin && !selectedCompany) {
      setItems([]);
      setMeta({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = { page, pageSize: 10 };
      if (q) params.q = q;
      if (isSuperAdmin && selectedCompany) params.companyId = selectedCompany;
      const { data } = await api.get('/products', { params });
      setItems(data.items);
      setMeta({ page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch { toast.error('Falha ao carregar produtos'); }
    finally { setLoading(false); }
  }, [q, isSuperAdmin, selectedCompany]);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/companies', { params: { page: 1, pageSize: 1000 } })
        .then(({ data }) => setCompanies(data.items || []))
        .catch(() => toast.error('Erro ao carregar empresas'));
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(1); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpenModal(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, warrantyMonths: Number(form.warrantyMonths) };
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post('/products', payload);
      toast.success(editing ? 'Produto atualizado' : 'Produto criado');
      setOpenModal(false); load(meta.page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/products/${confirmDel.id}`); toast.success('Produto excluído'); setConfirmDel(null); load(meta.page); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Package className="w-6 h-6 text-blue-600" /> Produtos</h1>
          <p className="text-sm text-slate-500">Catálogo de produtos com garantia.</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" disabled={isSuperAdmin && !selectedCompany}><Plus className="w-4 h-4 mr-2" /> Novo Produto</Button>
      </div>

      {isSuperAdmin && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Selecionar Empresa:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Selecione uma empresa..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.tradeName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por nome, modelo, SKU..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nenhum produto" icon={Package} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Modelo</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Garantia</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-3 text-slate-700">{p.model || '-'}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{p.sku || '-'}</td>
                    <td className="px-4 py-3 text-slate-700">{p.warrantyMonths} meses</td>
                    <td className="px-4 py-3"><ActiveBadge active={p.isActive} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(p)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>{editing ? 'Editar Produto' : 'Novo Produto'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Modelo</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            </div>
            <div><Label>Descrição</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Garantia (meses) *</Label>
                <Input type="number" value={form.warrantyMonths} onChange={(e) => setForm({ ...form, warrantyMonths: e.target.value })} />
              </div>
              <div>
                <Label>SLA (dias)</Label>
                <Input 
                  type="number" 
                  value={form.slaDeadlineDays || ''} 
                  onChange={(e) => setForm({ ...form, slaDeadlineDays: e.target.value })} 
                  placeholder="Ex: 15"
                />
                <p className="text-xs text-slate-500 mt-1">Prazo para envio ao cliente</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Excluir produto?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{confirmDel?.name} será desativado.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
